export type ContributionStatusFilter = 'current' | 'future' | 'ended';

export interface ContributionFilters {
  // Each list is empty when unset: empty means every client, category, funding source or status.
  client_id: number[];
  category_id: string[];
  funding_source_id: number[];
  status: ContributionStatusFilter[];
  period_from: string | null;
  period_to: string | null;
}

export const EMPTY_FILTERS: ContributionFilters = {
  client_id: [],
  category_id: [],
  funding_source_id: [],
  status: [],
  period_from: null,
  period_to: null,
};

export const STATUS_FILTER_OPTIONS: Array<{ id: ContributionStatusFilter; name: string }> = [
  { id: 'current', name: 'Current' },
  { id: 'future', name: 'Future' },
  { id: 'ended', name: 'Ended' },
];

const isBlank = (value: unknown) => value === null || value === undefined || value === '';

/** True when the period's From is after its To, which the API would reject. */
export const isPeriodRangeInvalid = ({
  period_from,
  period_to,
}: Pick<ContributionFilters, 'period_from' | 'period_to'>) => !!period_from && !!period_to && period_from > period_to;

/** Whether any filter is set (and so whether there is anything to clear). */
export const hasActiveFilters = (filters: ContributionFilters): boolean =>
  filters.client_id.length > 0 ||
  filters.category_id.length > 0 ||
  filters.funding_source_id.length > 0 ||
  filters.status.length > 0 ||
  !isBlank(filters.period_from) ||
  !isBlank(filters.period_to);

/**
 * The query params for the filters: only the ones that are set, each list comma-joined (a single value is just that value). A period
 * whose From is after its To is left out until it is corrected, rather than sent to be rejected.
 */
export const buildFilterParams = (filters: ContributionFilters): Record<string, string | number> => {
  const params: Record<string, string | number> = {};

  if (filters.client_id.length > 0) params.client_id = filters.client_id.join(',');
  if (filters.category_id.length > 0) params.category_id = filters.category_id.join(',');
  if (filters.funding_source_id.length > 0) params.funding_source_id = filters.funding_source_id.join(',');
  if (filters.status.length > 0) params.status = filters.status.join(',');

  if (!isPeriodRangeInvalid(filters)) {
    if (!isBlank(filters.period_from)) params.period_from = filters.period_from as string;
    if (!isBlank(filters.period_to)) params.period_to = filters.period_to as string;
  }

  return params;
};
